import { ChevronLeft, ChevronRight, HelpOutline, Warning } from '@mui/icons-material';
import { Button, IconButton, Stack, Tooltip, styled, useMediaQuery, useTheme } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridRowHeightParams,
  GridSortModel,
  gridPageSelector,
  gridPageSizeSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { YouTubeIcon } from '@zefr/style-guide/dist/ui/icons/index';
import getViewsAbbreviated from '@zefr/style-guide/dist/ui/utils/strings/getViewsAbbreviated';
import { useAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SORT_MODE_COUNTRY_POPULARITY } from '../atoms/encodedFormAtoms';
import { paginationModelAtom, tableSortAtom, tableStateAtom } from '../atoms/videoTableAtom';
import { useDemoForm } from '../context/DemoFormContext';
import { flagEmojiFromCode } from '../helpers/countryFlag';
import { computeDividerInsertionIndex } from '../helpers/sortState';
import { ProxyChannel, ProxyCountryRanking } from '../types/proxyApi';
import ChannelsLoadingOverlay from './ChannelsLoadingOverlay';
import ConceptsCell, { ConceptChip } from './DemoComponents/Form/VideoTableElements/ConceptsCell';
import VideoTableChannelIdCell from './DemoComponents/VideoTableChannelIdCell';
import VideoTableGarmScoreCell from './VideoTableGarmScoreCell';

const StyledDataGrid = styled(DataGrid)(({ theme }) => {
  return {
    backgroundColor: theme.palette.common.white,

    '--unstable_DataGrid-headWeight': 'bold',
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      fontWeight: 'bold',
      color: theme.palette.common.black,
    },
    '& .MuiDataGrid-cell': {
      padding: `${theme.spacing(2)}`,
      transition: theme.transitions.create(['height', 'max-height', 'min-height'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      }),
    },
    '& .MuiDataGrid-row': {
      transition: theme.transitions.create(['height', 'max-height', 'min-height'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      }),
    },
    '& .MuiDataGrid-cellContent': {
      height: '100%',
      textWrap: 'wrap',
      transition: theme.transitions.create(['height', 'max-height', 'min-height'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      }),
    },
    '& .scores': {
      '&.shrink .concept-chip:not(:first-of-type):not(#overflow-chip)': {
        display: 'none',
      },
      '&:not(.shrink) #overflow-chip.concept-chip': {
        display: 'none',
      },
    },
    '& .title, & .description, & .channel_summary': {
      fontWeight: '800',
    },
    '& .no-impressions-divider-cell': {
      borderTop: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.action.hover,
      cursor: 'default',
      // Cancel any inherited padding so the banner renderCell can manage
      // its own layout (icon + centered text).
      padding: 0,
    },
    '& .no-impressions-divider-cell:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  };
});

const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
    fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
  },
}));

// Mobile-only pagination: < Prev    (X-Y of Z Channels)    Next >
// Replaces MUI's compact icon-only toolbar so the page range and total are
// surfaced in plain English with thumb-sized targets.
export const MobilePagination: React.FC = () => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  // `gridPageCountSelector` divides the grid's *loaded* rows by pageSize,
  // which under server pagination only ever sees the current page's slice
  // — so it would clamp pageCount to 1 and disable Next forever. Compute
  // from the controlled `rowCount` prop instead.
  const pageCount = pageSize > 0 ? Math.ceil(rowCount / pageSize) : 0;
  const start = rowCount === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, rowCount);
  const isFirst = page === 0;
  const isLast = pageCount === 0 || page >= pageCount - 1;
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 1, py: 0.75, gap: 1, width: '100%', boxSizing: 'border-box' }}
    >
      <Button
        size="small"
        startIcon={<ChevronLeft />}
        disabled={isFirst}
        onClick={() => apiRef.current.setPage(page - 1)}
        sx={{ minWidth: 0 }}
      >
        Prev
      </Button>
      <Box
        sx={{
          flex: 1,
          textAlign: 'center',
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        {rowCount === 0 ? 'No channels' : `${start}–${end} of ${rowCount} Channels`}
      </Box>
      <Button
        size="small"
        endIcon={<ChevronRight />}
        disabled={isLast}
        onClick={() => apiRef.current.setPage(page + 1)}
        sx={{ minWidth: 0 }}
      >
        Next
      </Button>
    </Stack>
  );
};

export function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg width="120" height="100" viewBox="0 0 184 152" aria-hidden focusable="false">
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
            <path
              className="ant-empty-img-1"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="ant-empty-img-2"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="ant-empty-img-3"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
            />
          </g>
          <path
            className="ant-empty-img-3"
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
          />
          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1 }}>No Rows</Box>
    </StyledGridOverlay>
  );
}
// Synthetic row id used for the "no impressions" divider when sorting by
// Country Popularity. Kept distinct from any real channel_id.
export const NO_IMPRESSIONS_DIVIDER_ID = '__no-country-impressions-divider__';
export const NO_IMPRESSIONS_DIVIDER_TEXT =
  'The following channels do not have impressions for the selected countries. They are ranked based on their likelihood to serve impressions in the selected countries.';
// `colSpan` value used on the divider row's first cell to make it span
// the entire table width. MUI X DataGrid clamps this to the real column
// count, so any sufficiently-large number works; using a named constant
// avoids the magic number and makes intent clear.
const FULL_ROW_COL_SPAN = 99;

export interface CountryPopularityCellProps {
  rankings: ProxyCountryRanking[];
  isExclusion: boolean;
  expanded: boolean;
  /**
   * Optional callback invoked when the user clicks the "{n} more" overflow
   * chip. Lets callers (e.g. the mobile card column) toggle the expanded
   * state directly without relying on the surrounding DataGrid cell-click
   * handler — tap events on the chip don't always bubble there.
   */
  onToggleExpanded?: () => void;
}

export const CountryPopularityCell: React.FC<CountryPopularityCellProps> = ({
  rankings,
  isExclusion,
  expanded,
  onToggleExpanded,
}) => {
  const { countries: selectedCountries, sortMode } = useDemoForm();
  const MAX_VISIBLE = 2;
  // Push rankings whose country_name matches the active country filter to the
  // top, preserving rank order within each group.
  const sortedRankings = useMemo(() => {
    const selected = new Set(selectedCountries || []);
    if (selected.size === 0) return rankings;
    const matched: ProxyCountryRanking[] = [];
    const rest: ProxyCountryRanking[] = [];
    for (const r of rankings) {
      (selected.has(r.country_name) ? matched : rest).push(r);
    }
    return [...matched, ...rest];
  }, [rankings, selectedCountries]);
  if (sortedRankings.length === 0) {
    // When the user is sorting by country popularity but this channel has no
    // impression data in any of the selected countries, surface that
    // explicitly with an N/A + tooltip rather than rendering a blank cell.
    // (Outside country-sort mode the column is simply empty as before.)
    if (sortMode === SORT_MODE_COUNTRY_POPULARITY) {
      return (
        <Stack direction="row" alignItems="center" gap={0.5} height="100%">
          <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>N/A</Box>
          <Tooltip
            title="This channel does not have impressions for the selected countries."
            arrow
            placement="top"
          >
            <IconButton
              size="small"
              aria-label="No country impression data — fallback ranking explanation"
              sx={{ p: 0, color: 'text.secondary', cursor: 'help' }}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }
    return null;
  }
  const visible = expanded ? sortedRankings : sortedRankings.slice(0, MAX_VISIBLE);
  const hiddenCount = sortedRankings.length - visible.length;
  return (
    <Stack
      gap={1}
      direction="row"
      flexWrap="wrap"
      justifyContent="flex-start"
      alignContent="flex-start"
      height="100%"
      py={1}
      boxSizing="border-box"
    >
      {visible.map((r) => {
        const flag = flagEmojiFromCode(r.country_code);
        const flagPrefix = flag ? `${flag} ` : '';
        return (
          <ConceptChip
            key={r.country_name}
            label={`${flagPrefix}${r.country_name}: ${r.final_rank}`}
            color={isExclusion ? 'error' : 'secondary'}
          />
        );
      })}
      {hiddenCount > 0 && (
        <ConceptChip
          id="country-popularity-overflow-chip"
          color={isExclusion ? 'error' : 'secondary'}
          // Wire the chip as a clickable expander when the caller provides
          // a toggle. Without it the chip is purely decorative and the
          // surrounding DataGrid cell-click is expected to do the toggle.
          // Don't pass `sx` here — props.sx would replace ConceptChip's
          // own sx (which sets `height: 'fit-content'`) and the chip
          // would inflate to MUI Chip's default 32px clickable height.
          onClick={
            onToggleExpanded
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onToggleExpanded();
                }
              : undefined
          }
          label={
            <Box display="flex" justifyContent="center" alignItems="center">
              {hiddenCount} more <ChevronRight fontSize="inherit" />
            </Box>
          }
        />
      )}
    </Stack>
  );
};

export interface LanguageCellProps {
  language: string | undefined;
  isExclusion: boolean;
}

export const LanguageCell: React.FC<LanguageCellProps> = ({ language, isExclusion }) => {
  if (!language) return null;
  return (
    <Stack
      direction="row"
      gap={1}
      flexWrap="wrap"
      alignContent="flex-start"
      justifyContent="flex-start"
      height="100%"
      py={1}
      boxSizing="border-box"
    >
      <ConceptChip label={language} color={isExclusion ? 'error' : 'secondary'} />
    </Stack>
  );
};

export interface CountryPopularityHeaderProps {
  onResetSort?: () => void;
  isActive?: boolean;
}

export const CountryPopularityHeader: React.FC<CountryPopularityHeaderProps> = ({
  onResetSort,
  isActive,
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (onResetSort) onResetSort();
    },
    [onResetSort],
  );
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      onClick={handleClick}
      role={onResetSort ? 'button' : undefined}
      tabIndex={onResetSort ? 0 : undefined}
      onKeyDown={
        onResetSort
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onResetSort();
              }
            }
          : undefined
      }
      aria-label={onResetSort ? 'Sort by country popularity' : undefined}
      sx={{
        cursor: onResetSort ? 'pointer' : 'default',
        userSelect: 'none',
        textDecoration: isActive ? 'underline' : 'none',
      }}
    >
      <Box sx={{ fontWeight: 'bold' }}>Country Popularity</Box>
      <Tooltip
        title="Channel Rankings are based on Overall Impressions from that Country before any concept filters applied. Click to sort by this column."
        arrow
        placement="top"
      >
        <IconButton
          size="small"
          aria-label="Country popularity ranking help"
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0, color: 'text.secondary', cursor: 'help' }}
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export interface VideoTableProps extends BoxProps {
  rows?: ProxyChannel[];
  totalRows?: number;
  isExclusion?: boolean;
  isLoading?: boolean;
}

interface ClampableTextProps {
  children: React.ReactNode;
  lines?: number;
  sx?: object;
}

// Block of text that clamps to a fixed line count and toggles to its full
// height on click / Enter / Space. When the clamped render overflows, an
// inline " ...more" suffix is appended after the line-clamp's natural
// trailing ellipsis so the trail-off reads as "text... ...more". The
// whole block is the toggle target — clicking anywhere on it
// expands/collapses. stopPropagation keeps the surrounding DataGrid cell
// click (which toggles country-chip expansion) from firing.
export const ClampableText: React.FC<ClampableTextProps> = ({ children, lines = 3, sx }) => {
  const [expanded, setExpanded] = useState(false);
  // Only re-measured while collapsed; the expanded view always fits since
  // we drop the clamp.
  const [hadOverflow, setHadOverflow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const toggle = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);
  useLayoutEffect(() => {
    if (expanded) return undefined;
    const el = ref.current;
    /* istanbul ignore next -- useLayoutEffect always runs after the
       ref is attached, so this null guard is purely defensive and is
       not reachable through normal React lifecycle. */
    if (!el) return undefined;
    const update = () => {
      setHadOverflow(el.scrollHeight > el.clientHeight + 1);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    return undefined;
  }, [children, expanded, lines]);
  return (
    <Box
      ref={ref}
      onClick={toggle}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(e);
        }
      }}
      sx={{
        cursor: 'pointer',
        ...(expanded
          ? {}
          : {
              display: '-webkit-box',
              WebkitLineClamp: lines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }),
        ...(sx ?? {}),
      }}
    >
      {children}
      {!expanded && hadOverflow && ' ...more'}
    </Box>
  );
};

interface LabeledSectionProps {
  label: string;
  tooltip?: string;
  children: React.ReactNode;
}

// Compact bordered row: label on the left (with optional help icon), the
// section's chips on the right of the same line. No background to avoid
// clashing with the row's highlight/select tint.
export const LabeledSection: React.FC<LabeledSectionProps> = ({ label, tooltip, children }) => (
  <Box
    sx={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      px: 0.75,
      py: 0.5,
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    <Stack direction="row" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
      <Box
        sx={{
          fontSize: '0.5rem',
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
      {tooltip && (
        <Tooltip title={tooltip} arrow placement="top">
          <IconButton
            size="small"
            aria-label={`${label} help`}
            sx={{ p: 0, color: 'text.secondary', cursor: 'help' }}
          >
            <HelpOutline sx={{ fontSize: '0.7rem' }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

interface MobileChannelCardProps {
  row: ProxyChannel;
  isExclusion: boolean;
  apiCountries: Record<string, { country_code?: string }> | undefined;
  countryChipsExpanded: boolean;
  onToggleCountryChipsExpanded?: () => void;
}

const MobileChannelCard: React.FC<MobileChannelCardProps> = ({
  row,
  isExclusion,
  apiCountries,
  countryChipsExpanded,
  onToggleCountryChipsExpanded,
}) => {
  const country = row.country;
  const dailyViewsText = getViewsAbbreviated(row.average_daily_views, true);
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
      <Stack direction="row" gap={1.25} alignItems="flex-start">
        <Box
          sx={{
            flexShrink: 0,
            '& img': { maxWidth: 120, maxHeight: 75 },
          }}
        >
          <VideoTableChannelIdCell channelId={row.channel_id} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              fontSize: '0.9rem',
              fontWeight: 800,
              lineHeight: 1.25,
              wordBreak: 'break-word',
            }}
          >
            {row.title}
          </Box>
          {row.description && (
            <ClampableText
              lines={1}
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {row.description}
            </ClampableText>
          )}
          {country && (
            <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>
              Published In:{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {flagEmojiFromCode(apiCountries?.[country]?.country_code)} {country}
              </Box>
            </Box>
          )}
          {row.language && (
            <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>
              Primary Language:{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {row.language}
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <VideoTableGarmScoreCell
            values={row.garm_levels as unknown as Record<string, string>}
            videoCounts={row.garm_risk_video_counts}
          />
        </Box>
      </Stack>
      {row.country_rankings && row.country_rankings.length > 0 && (
        <LabeledSection
          label="Country Popularity"
          tooltip="Channel rankings are based on Overall Impressions from that country before any concept filters applied."
        >
          <CountryPopularityCell
            rankings={row.country_rankings}
            isExclusion={isExclusion}
            expanded={countryChipsExpanded}
            onToggleExpanded={onToggleCountryChipsExpanded}
          />
        </LabeledSection>
      )}
      {row.scores && Object.keys(row.scores).length > 0 && (
        <LabeledSection
          label="Concept Score"
          tooltip="Concept Score is a number from 1 - 100 indicating how likely the Channel will produce content related to that Concept."
        >
          <ConceptsCell
            values={row.scores}
            isExclusion={isExclusion}
            expanded={countryChipsExpanded}
            onToggleExpanded={onToggleCountryChipsExpanded}
          />
        </LabeledSection>
      )}
      {row.channel_summary && (
        <ClampableText lines={3} sx={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', mr: 0.5 }}>
            Zefr Details:
          </Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {stripZefrDetailsPrefix(row.channel_summary)}
          </Box>
        </ClampableText>
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
      >
        {isExclusion && <Warning sx={{ color: '#FA6F4F', fontSize: '0.95rem' }} />}
        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {dailyViewsText}
        </Box>
        <Box component="span">avg daily views</Box>
      </Stack>
    </Box>
  );
};

// Real DataGrid columns shown on md+ but hidden on xs in favor of a single
// card column that stacks each row's content vertically. A pure-list
// presentation crams too much horizontal data into a 375px viewport.
const MOBILE_HIDDEN_COLUMNS = [
  'id',
  'title',
  'description',
  'channel_summary',
  'scores',
  'language',
  'country_rankings',
  'average_daily_views',
  'garm_levels',
] as const;
// On md+ the channel column carries thumbnail + title + "Published In" +
// avg-daily-views stacked vertically. The standalone Title and Avg Daily
// Views columns are then redundant — hiding them frees horizontal room so
// the chip-based columns (Concept Score, Country Popularity) can keep
// their chips on a single line at common widths.
const DESKTOP_HIDDEN_COLUMNS = ['title', 'average_daily_views'] as const;
// Field id of the synthetic xs-only card column.
export const MOBILE_CARD_COLUMN_FIELD = '__mobile_card';

// Backend often emits Zefr Details (channel_summary) prefixed with a
// numbered list marker like "1: " (sometimes "2: " etc.). Strip a leading
// digit-run + colon + whitespace so the displayed text starts on the real
// content. Conservative match: only a leading digit-then-colon followed by
// at least one whitespace character is stripped, so normal copy that
// happens to contain a colon early on is left alone.
const ZEFR_DETAILS_PREFIX_RE = /^\d+:\s+/;
export const stripZefrDetailsPrefix = (value: string | undefined): string | undefined =>
  typeof value === 'string' ? value.replace(ZEFR_DETAILS_PREFIX_RE, '') : value;

export const buildMobileColumnVisibilityModel = (isMobile: boolean): GridColumnVisibilityModel => {
  if (!isMobile) {
    return {
      [MOBILE_CARD_COLUMN_FIELD]: false,
      ...Object.fromEntries(DESKTOP_HIDDEN_COLUMNS.map((field) => [field, false])),
    };
  }
  return {
    ...Object.fromEntries(MOBILE_HIDDEN_COLUMNS.map((field) => [field, false])),
    [MOBILE_CARD_COLUMN_FIELD]: true,
  };
};

const VideoTable: React.FC<VideoTableProps> = ({
  rows: myRows = [],
  totalRows = 0,
  isExclusion = false,
  isLoading = false,
  ...props
}) => {
  const { apiCountries, countries: selectedCountries, sortMode, setSortMode } = useDemoForm();
  const theme = useTheme();
  // Below `lg` the desktop table can't shrink any further (the chip
  // columns hit their min widths and chips start wrapping in awkward ways)
  // — fall back to the card layout that handles narrow viewports cleanly.
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const columnVisibilityModel = useMemo(
    () => buildMobileColumnVisibilityModel(isMobile),
    [isMobile],
  );
  const [paginationModel, setPaginationModel] = useAtom(paginationModelAtom);
  const [tableState, setTableState] = useAtom(tableStateAtom);
  const [tableSort, setTableSort] = useAtom(tableSortAtom);
  const handleResetToCountrySort = useCallback(() => {
    setSortMode(SORT_MODE_COUNTRY_POPULARITY);
    setTableSort(RESET);
  }, [setSortMode, setTableSort]);
  const isCountrySortActive = sortMode === SORT_MODE_COUNTRY_POPULARITY;
  // Controlled sort model. The atom stores 'avg_daily_views' (the API field
  // name) but the visible column id is 'average_daily_views', so map back when
  // building the model the DataGrid renders.
  const sortModel: GridSortModel = useMemo(() => {
    if (!tableSort) return [];
    if (tableSort.field === 'avg_daily_views') {
      return [{ field: 'average_daily_views', sort: tableSort.sort }];
    }
    return [tableSort];
  }, [tableSort]);
  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      // Here you save the data you need from the sort model
      if (sortModel.length > 0) {
        // The API sends down 'average_daily_views' but expects us to send back 'avg_daily_views'
        if (sortModel[0].field === 'average_daily_views') {
          setTableSort({ field: 'avg_daily_views', sort: sortModel[0].sort });
        } else {
          setTableSort(sortModel[0]);
        }
      } else {
        setTableSort(RESET);
      }
    },
    [setTableSort],
  );

  const handleGetCellClassName = useCallback(
    ({ id, field, row }: GridCellParams) => {
      if (row?.__isDivider) return 'no-impressions-divider-cell';
      const newClassName = field;
      if (!tableState[id]) {
        return `${newClassName} shrink`;
      }
      return newClassName;
    },
    [tableState],
  );
  const transformedRows = useMemo(() => {
    const results: Record<string, unknown>[] = myRows.map((r) => ({
      id: r.channel_id,
      ...r,
    }));
    // Divider injection only applies when sorting by Country Popularity.
    // `computeDividerInsertionIndex` handles the partition rules (clean
    // partition vs interleaved) and returns null when no divider should
    // be drawn on this page. See `helpers/sortState.ts` for the rationale.
    if (!isCountrySortActive) return results;
    const idx = computeDividerInsertionIndex(
      results as { country_rankings?: ProxyCountryRanking[] }[],
      selectedCountries,
    );
    if (idx === null) return results;
    const divider: Record<string, unknown> = {
      id: NO_IMPRESSIONS_DIVIDER_ID,
      __isDivider: true,
    };
    return [...results.slice(0, idx), divider, ...results.slice(idx)];
  }, [myRows, isCountrySortActive, selectedCountries]);

  // TODO: See if we want to both with this pro-feature, or if we already have a license
  // Pro feature
  // const handleRowMouseEnter: GridEventListener<'rowMouseEnter'> = useCallback(
  //   ({ id }) => {
  //     setTableState((oldState) => {
  //       const newState: Record<string, boolean> = { ...oldState };
  //       newState[id] = true;
  //       return newState;
  //     });
  //   },
  //   [setTableState],
  // );

  // const handleRowMouseLeave: GridEventListener<'rowMouseLeave'> = useCallback(
  //   ({ id }) => {
  //     setTableState((oldState) => {
  //       const newState: Record<string, boolean> = { ...oldState };
  //       newState[id] = false;
  //       return newState;
  //     });
  //   },
  //   [setTableState],
  // );
  // useGridApiEventHandler(apiRef, 'rowMouseEnter', handleRowMouseEnter);
  // useGridApiEventHandler(apiRef, 'rowMouseLeave', handleRowMouseLeave);
  const getRowHeight = useCallback(
    ({ id, model }: GridRowHeightParams) => {
      // Card layout sizes itself to its content; fixed-height rows would
      // clip chips, country sub-lines, and the wrapped divider banner.
      if (isMobile) return 'auto';
      if (model?.__isDivider) return 56;
      // Default row height accommodates the Channels column's vertical
      // stack: thumbnail (~75px) + title + "Published In" + avg daily
      // views. Expanded rows still go to 'auto' so chip overflow can grow.
      return tableState[id] ? 'auto' : 140;
    },
    [isMobile, tableState],
  );

  const onCellClick: GridEventListener<'cellClick'> = useCallback(
    ({ field, row }) => {
      if (row?.__isDivider) return;
      if (['id', 'garm_levels'].indexOf(field) === -1) {
        setTableState((oldState) => {
          const newState: Record<string, boolean> = { ...oldState };
          newState[row.id] = !newState[row.id];
          return newState;
        });
      }
    },
    [setTableState],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Channels',
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        headerAlign: 'center',
        // Wide enough for thumbnail (150px max) + title text below without
        // truncating most channel names. Chip columns get more horizontal
        // room because Title / Avg Daily Views are folded in here.
        minWidth: 180,
        align: 'center',
        // For the synthetic divider row, span the full table width so the
        // explanatory banner spreads across every column. Real rows behave
        // normally (one cell each).
        colSpan: ({ row }) => (row?.__isDivider ? FULL_ROW_COL_SPAN : 1),
        renderCell: (params) => {
          if (params.row?.__isDivider) {
            return (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{
                  width: '100%',
                  height: '100%',
                  px: 2,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                  textAlign: 'center',
                }}
              >
                <HelpOutline fontSize="small" />
                <Box>{NO_IMPRESSIONS_DIVIDER_TEXT}</Box>
              </Stack>
            );
          }
          const row = params.row as ProxyChannel;
          const country = row.country;
          const dailyViewsText = getViewsAbbreviated(row.average_daily_views, true);
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.5,
                py: 1,
              }}
            >
              <VideoTableChannelIdCell channelId={row.channel_id} />
              <Box
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                }}
              >
                {row.title}
              </Box>
              {country && (
                <Box
                  sx={{
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    lineHeight: 1.3,
                    textAlign: 'center',
                  }}
                >
                  Published In: {flagEmojiFromCode(apiCountries?.[country]?.country_code)} {country}
                </Box>
              )}
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
              >
                {isExclusion && <Warning sx={{ color: '#FA6F4F', fontSize: '0.9rem' }} />}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {dailyViewsText}
                </Box>
                <Box component="span">avg daily views</Box>
              </Stack>
            </Box>
          );
        },
        renderHeader: () => {
          return (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={1}
              sx={{ '& *': { filter: 'grayscale(1)' } }}
            >
              <YouTubeIcon /> Channels
            </Box>
          );
        },
      },
      {
        // Hidden on md+ via the visibility model (folded into the Channels
        // column). Kept in the columns array so its row data is still
        // available to the renderer above and so the column id remains
        // valid for any historical sort-state.
        field: 'title',
        headerName: 'Title',
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'description',
        headerName: 'YouTube Details',
        sortable: true,
        flex: 1.4,
        minWidth: 180,
      },
      {
        field: 'channel_summary',
        headerName: 'Zefr Details',
        sortable: true,
        flex: 2,
        minWidth: 220,
        valueFormatter: ({ value }) => stripZefrDetailsPrefix(value as string | undefined),
      },
      {
        field: 'scores',
        headerName: 'Concept Score',
        // Single "Concept: 99" chip is ~110px on one line.
        minWidth: 110,
        sortable: false,
        filterable: false,
        renderHeader: () => (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Box sx={{ fontWeight: 'bold' }}>Concept Score</Box>
            <Tooltip
              title="Concept Score is a number from 1 - 100 indicating how likely the Channel will produce content related to that Concept."
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                aria-label="Concept Score help"
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0, color: 'text.secondary', cursor: 'help' }}
              >
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
        renderCell: (params) => {
          const value = params.value as Record<string, number>;
          return <ConceptsCell values={value} isExclusion={isExclusion} />;
        },
        flex: 1.2,
      },
      {
        field: 'language',
        headerName: 'Primary Language',
        // The "Primary Language" header text + sort indicator needs
        // ~130px of width to render fully without truncation.
        minWidth: 130,
        sortable: true,
        filterable: false,
        renderCell: (params) => (
          <LanguageCell language={params.value as string | undefined} isExclusion={isExclusion} />
        ),
        flex: 0.7,
      },
      {
        field: 'country_rankings',
        headerName: 'Country Popularity',
        // Two chips like "🇹🇭 Thailand: 226" need ~150px to render on one
        // line without "Thailand: 226" wrapping inside the chip.
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderHeader: () => (
          <CountryPopularityHeader
            onResetSort={handleResetToCountrySort}
            isActive={isCountrySortActive}
          />
        ),
        renderCell: (params) => (
          <CountryPopularityCell
            rankings={(params.value as ProxyCountryRanking[] | undefined) || []}
            isExclusion={isExclusion}
            expanded={Boolean(tableState[params.id])}
          />
        ),
        flex: 1,
      },
      {
        field: 'average_daily_views',
        headerName: 'Avg. Daily Views',
        type: 'number',
        // Avg Daily Views is exposed as a Primary Sort option; offering a
        // duplicate column-header sort just confuses users (and the
        // column-sort path can fight with the Primary Sort selector). Keep
        // the column display-only.
        sortable: false,
        valueFormatter: ({ value }) => {
          return getViewsAbbreviated(value as number | undefined, true);
        },
        renderCell: ({ value }) => {
          const text = getViewsAbbreviated(value as number | undefined, true);
          return (
            <>
              <Warning
                sx={{
                  display: isExclusion ? undefined : 'none',
                  color: '#FA6F4F',
                  fontSize: '1rem',
                }}
              />
              {text}
            </>
          );
        },
        // valueGetter: (params) => {
        //   const value = params.value as Record<string, number>;
        //   const dailyViews = value['total_average_daily_views'] || undefined;
        //   return dailyViews;
        // },
        align: 'center',
      },
      {
        field: 'garm_levels',
        headerName: 'More',
        sortable: false,
        disableColumnMenu: false,
        filterable: false,
        headerAlign: 'center',
        resizable: false,
        width: 60,
        // valueGetter: (params: GridValueGetterParams) => `...`,
        renderCell: ({ value, row }) => (
          <VideoTableGarmScoreCell
            values={value}
            videoCounts={(row as ProxyChannel).garm_risk_video_counts}
          />
        ),
      },
      {
        // Single column that takes the whole row width on xs and renders a
        // card-like layout instead of a dense table. Hidden on md+ via the
        // visibility model.
        field: MOBILE_CARD_COLUMN_FIELD,
        headerName: '',
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        resizable: false,
        flex: 1,
        minWidth: 0,
        colSpan: ({ row }) => (row?.__isDivider ? FULL_ROW_COL_SPAN : 1),
        renderCell: (params) => {
          if (params.row?.__isDivider) {
            return (
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="center"
                gap={1}
                sx={{
                  width: '100%',
                  height: '100%',
                  px: 1,
                  py: 1,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  lineHeight: 1.3,
                  textAlign: 'center',
                }}
              >
                <HelpOutline fontSize="small" sx={{ flexShrink: 0, mt: 0.25 }} />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {NO_IMPRESSIONS_DIVIDER_TEXT}
                </Box>
              </Stack>
            );
          }
          return (
            <MobileChannelCard
              row={params.row as ProxyChannel}
              isExclusion={isExclusion}
              apiCountries={apiCountries}
              countryChipsExpanded={Boolean(tableState[params.id])}
              onToggleCountryChipsExpanded={() =>
                setTableState((oldState) => {
                  const newState: Record<string, boolean> = { ...oldState };
                  newState[params.id] = !newState[params.id];
                  return newState;
                })
              }
            />
          );
        },
      },
    ],
    [
      isExclusion,
      apiCountries,
      tableState,
      setTableState,
      handleResetToCountrySort,
      isCountrySortActive,
    ],
  );
  useEffect(() => {
    return () => {
      setTableState(RESET);
    };
  }, [setTableState]);
  const getRowId = useCallback((row: Record<string, unknown>) => {
    // Synthetic divider row has no channel_id — fall back to its synthetic id.
    return (row.channel_id as string) || (row.id as string);
  }, []);
  return (
    <Box {...props} sx={{ position: 'relative', ...(props.sx as object) }}>
      <StyledDataGrid
        rows={transformedRows}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnFilter={true}
        // Suppress the column header three-dot menu globally — its
        // default contents ("Hide column", "Manage columns") aren't
        // useful here and just clutter the headers.
        disableColumnMenu={true}
        density={'comfortable'}
        pageSizeOptions={[5, 10, 25, 100]}
        editMode="row"
        autoHeight={true}
        getCellClassName={handleGetCellClassName}
        autoPageSize={false}
        hideFooterSelectedRowCount
        onCellClick={onCellClick}
        getRowHeight={getRowHeight}
        pagination={true}
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={paginationModel}
        sortingMode="server"
        sortModel={sortModel}
        getRowId={getRowId}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={setPaginationModel}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          ...(isMobile ? { pagination: MobilePagination } : {}),
        }}
        sx={(theme) => ({
          '--DataGrid-overlayHeight': '300px',
          [theme.breakpoints.down('lg')]: {
            // No table headers on the card layout — the card column has no
            // header text and the grid header chrome would just take
            // vertical space.
            '& .MuiDataGrid-columnHeaders': {
              display: 'none',
            },
            // Cards manage their own padding; let the cell wrap naturally.
            '& .MuiDataGrid-cell': {
              padding: theme.spacing(1, 1.5),
              alignItems: 'flex-start',
              whiteSpace: 'normal',
            },
            // Suppress hover / selected row tints — selection is a no-op
            // on the card layout, the highlight just clashes with the
            // card content.
            '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          },
        })}
      />
      {isLoading && <ChannelsLoadingOverlay />}
    </Box>
  );
};

export default VideoTable;
